import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import Notification from "../../components/UI/Notification";
import { getToken } from "../../../src/utils/tokenUtils";
import Drawer from "../../components/UI/Drawer";
import ReactPaginate from 'react-paginate';
import { CheckCircle, XCircle, User, Calendar, Briefcase, Clock } from 'lucide-react';
import { Link } from "react-router-dom";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";

const LeaveApproverPage = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [filter, setFilter] = useState("All");
  const token = getToken();
  const [pagination, setPagination] = useState({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 15,
      });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total_days, setTotalDays] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  
  // filter
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [filteredData, setFilteredData] = useState(leaveRequests);

  const [tempFilters, setTempFilters] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState(""); // "approved" or "rejected"
  const [approverComments, setApproverComments] = useState("");

  const { roles } = useContext(AuthContext);
  const getRoleBasedRouteLocal = (route) => getRoleBasedRoute(route, roles);
  
  const toggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu);
  };

  const handleFilterChange = (field, value) => {
    setActiveFilters(prev => ({
    ...prev,
    [field]: value
    }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery("");
  };

  const clearFilterByKey = (key) => {
    const updatedFilters = { ...activeFilters };
    delete updatedFilters[key];
    setActiveFilters(updatedFilters);
  };

  const filterableColumns = [
    { field: "request_date", label: "Request Date" },
    { field: "requestable_type", label: "Request Type" },
    { field: "status", label: "status" },
  ];

  useEffect(() => {
  if (showFilterMenu) {
      setTempFilters(activeFilters);
  }
  }, [showFilterMenu]);

  useEffect(() => {
    fetchLeaveRequests();
  }, [token]);

  useEffect(() => {
      // Apply both search query and filters
      let result = leaveRequests;

      // Apply search query filtering
      if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      result = result.filter((item) => {
          // Search across all relevant fields
          return (
          (item.user.name?.toLowerCase().includes(lowercasedQuery)) ||
          (item.request_date?.toLowerCase().includes(lowercasedQuery)) ||
          (item.requestable_type?.toString().toLowerCase().includes(lowercasedQuery)) ||
          (item.status?.toLowerCase().includes(lowercasedQuery))
          );
      });
      }

      // Apply column-specific filters
      Object.entries(activeFilters).forEach(([field, filterValue]) => {
      if (filterValue) {
          result = result.filter(item => {
          const value = item[field];
          if (field === 'created_at' && value) {
              // Date filtering
              const itemDate = new Date(value);
              const day = String(itemDate.getDate()).padStart(2, '0');
              const month = String(itemDate.getMonth() + 1).padStart(2, '0');
              const year = itemDate.getFullYear();
              const formattedDate = `${day}-${month}-${year}`;
              
              return formattedDate.includes(filterValue);
          } else {
              // Standard string filtering
              return value?.toString().toLowerCase().includes(filterValue.toLowerCase());
          }
          });
      }
      });

      const filteringNow = searchQuery.length > 0 || Object.values(activeFilters).some(v => !!v);
      setIsFiltering(filteringNow);
      setFilteredData(result);
  }, [searchQuery, activeFilters, leaveRequests]);

  const fetchLeaveRequests = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://hrmsnew.prepstripe.com/api/approvals?page=${page}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      const data = response.data; // Ensure correct data structure
      setLeaveRequests(data?.data || []);
      setLoading(false);
      setPagination({
        currentPage: data?.current_page || 1,
        totalPages: data?.last_page || 1,
        totalItems: data?.total || 0,
        perPage: data?.per_page || 10,
      });
  
    } catch (error) {
      console.error("Error fetching leave requests", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleApproveReject = async (id, status) => {
    try {
      const response = await axios.put(
        `https://hrmsnew.prepstripe.com/api/approvals/${id}`,
        { status, approver_comments: approverComments, },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchLeaveRequests();
      setDrawerOpen(false);
      // setNotification("Request status updated successfully");
      setNotification({
        message: "Request status updated successfully",
        type: "success",
      });
    } catch (error) {
      // console.log("Error response:", error?.response?.data?.error);
      // console.error("Error updating status", error);
      // setNotification({
      //   message: error?.response?.data?.error,
      //   type: "error",
      // });
      const errorMsg = error?.response?.data?.error || error.message || "An error occurred";
      setNotification({ message: errorMsg, type: "error" });
    }
  };
  

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchLeaveRequests(newPage);
    }
  };

  // console.log("Total Pages", pagination?.totalPages)

  const renderPageNumbers = () => {
      const pages = [];
      for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-4 py-2 rounded-md mx-1 ${
              currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {i}
          </button>
        );
      }
      return pages;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const filtering = value !== '';
    setIsFiltering(filtering);
  
    if (!filtering) {
      // Search cleared, restore current page's data
      handlePageChange(currentPage); // fetchResponses will be called inside
    }
  };
  

  // Filtered Leaves
  const filteredRequests = leaveRequests
  .filter((leave) => {
    if (filter === "All") return true;
    return leave.status.toLowerCase() === filter.toLowerCase();
  })
  .filter((leave) => {
    if (!searchTerm) return true;

    const lowerSearch = searchTerm.toLowerCase();

    return (
      (leave.user.name && leave.user.name.toLowerCase().includes(lowerSearch)) ||
      (leave.requestable_type && leave.requestable_type.toLowerCase().includes(lowerSearch)) ||
      (leave.status && leave.status.toLowerCase().includes(lowerSearch)) ||
      (leave.request_date && leave.request_date.toLowerCase().includes(lowerSearch))
    );
  });

  const hasTempFilters = Object.values(tempFilters).some((val) => val !== "");

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg className="w-3 h-3 text-gray-400 mx-1" aria-hidden="true" fill="none" viewBox="0 0 6 10">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">Leave Requests</span>
            </div>
          </li>
        </ol>
      </nav>

      <h2 className="text-lg sm:text-xl dark:text-white font-semibold mb-4">Leave Requests</h2>

      {/* Search and filter controls */}
      <div className="flex flex-col sm:flex-row gap-2 justify-between items-start mb-4">

        {/* search field */}
        <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full dark:bg-black dark:text-white dark:border-gray-700 sm:w-64 placeholder:text-xs md:placeholder:text-sm p-2 border border-gray-300 rounded-md"
        />

        {/* filter button */}
        <div className="flex justify-between gap-2 w-full sm:w-auto">
            <div className="relative">
                <button 
                    onClick={toggleFilterMenu}
                    className="p-2 dark:bg-gray-800 dark:text-white dark:border-gray-700 border border-[#F4F7FF] rounded-md flex gap-1 items-center bg-[#F4F7FF] hover:bg-gray-200"
                    >
                    <svg className="dark:text-white" width="18" height="17" viewBox="0 0 13 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.98734 13.5608C7.85034 13.5608 7.71598 13.5252 7.59884 13.4581L5.09834 12.0415C4.817 11.8812 4.58412 11.6552 4.42215 11.3852C4.26017 11.1153 4.17457 10.8104 4.17359 10.5001V7.29493C4.17408 7.06871 4.0996 6.84803 3.96059 6.6638L0.469345 2.02988C0.387822 1.92291 0.339174 1.79674 0.328769 1.66531C0.318364 1.53387 0.346605 1.40228 0.410378 1.28504C0.474151 1.16781 0.570975 1.06949 0.690169 1.00093C0.809362 0.932381 0.94629 0.896259 1.08584 0.896552H11.8333C11.973 0.896002 12.1101 0.931967 12.2295 1.00047C12.3488 1.06896 12.4458 1.16732 12.5096 1.28465C12.5734 1.40198 12.6016 1.5337 12.5911 1.66523C12.5805 1.79677 12.5317 1.92297 12.4498 2.02988L8.95859 6.6638C8.81933 6.84794 8.74458 7.06863 8.74484 7.29493V12.844C8.74465 13.0338 8.66482 13.2158 8.52285 13.3502C8.38088 13.4845 8.18832 13.5603 7.98734 13.5608ZM1.08584 1.60843L4.57109 6.25297C4.80208 6.55645 4.92547 6.92118 4.92359 7.29493V10.4994C4.92387 10.6874 4.9756 10.8722 5.07376 11.0358C5.17192 11.1994 5.31319 11.3362 5.48384 11.433L7.98434 12.8497L7.99484 7.29422C7.99319 6.92039 8.11685 6.55566 8.34809 6.25226L11.8386 1.61834L1.08584 1.60843Z" fill="currentColor"/>
                    </svg>
                    <span className="ml-1">Filter</span>
                </button>
                
                {showFilterMenu && (
                <div className="absolute dark:bg-gray-900 dark:border-gray-700 z-10 mt-2 md:right-0 right-auto left-0 md:left-auto bg-white border border-gray-200 rounded-md shadow-lg p-4 w-64">
                    <div className="flex justify-between items-center mb-3">
  
                    {hasTempFilters && (
                        <button
                            onClick={() => {
                            setActiveFilters(tempFilters);
                            setShowFilterMenu(false);
                            }}
                            className=" bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 text-sm"
                        >
                            Apply Filters
                        </button>
                    )}
                    </div>
                    
                    <div className="space-y-3">
                    {filterableColumns.map((column) => (
                        <div key={column.field} className="space-y-1">
                        <label className="block text-sm dark:text-gray-400 font-medium text-gray-700">
                            {column.label}
                        </label>
                        {column.field === "status" ? (
                            // Dropdown for status filter
                            <select
                            value={tempFilters[column.field] || ""}
                            onChange={(e) => {
                                const value = e.target.value;
                                setTempFilters(prev => ({ ...prev, [column.field]: value }));
                            }}
                            // onChange={(e) => handleFilterChange(column.field, e.target.value)}
                            className="w-full dark:bg-black dark:text-white dark:border-gray-700 p-2 text-sm border border-gray-300 rounded-md"
                            >
                            <option value="">All</option>
                            <option value="approved">Approved</option>
                            <option value="pending">Pending</option>
                            </select>
                         ) : column.field === "request_date" ? (
                            <input
                                type="date"
                                // value={activeFilters[column.field] || ""}
                                value={tempFilters[column.field] || ""}
                                // onChange={(e) =>
                                //     handleFilterChange(column.field, e.target.value) // Store as yyyy-MM-dd
                                // }
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setTempFilters(prev => ({ ...prev, [column.field]: value }));
                                }}
                                className="w-full dark:bg-gray-950 dark:border-gray-700 dark:text-white p-2 text-sm border border-gray-300 rounded-md"
                            />
                        ) : (
                            // Text input for other filters
                            <input
                            type="text"
                            value={tempFilters[column.field] || ""}
                            onChange={(e) => {
                                const value = e.target.value;
                                setTempFilters(prev => ({ ...prev, [column.field]: value }));
                            }}
                            // onChange={(e) => handleFilterChange(column.field, e.target.value)}
                            placeholder={`Filter by ${column.label.toLowerCase()}`}
                            className="w-full dark:bg-black dark:text-white dark:border-gray-700 p-2 text-sm border border-gray-300 rounded-md"
                            />
                        )}
                        </div>
                    ))}
                    </div>
                    
                    {isFiltering && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                        Showing {filteredData.length} of {leaveRequests.length} results
                        </p>
                    </div>
                    )}
                </div>
                )}
            </div>

            {isFiltering && (
            <div className="flex flex-wrap gap-2">
                {Object.entries(activeFilters).map(([key, value]) => (
                value && (
                    <div
                    key={key}
                    className="flex items-center gap-2 px-3 py-1.5 border border-blue-500 bg-blue-50 dark:bg-gray-900 text-blue-600 rounded-full text-sm shadow-sm"
                    >
                    <span className="capitalize dark:text-white">{key.replace('_', ' ')}: {value}</span>
                    <button
                        onClick={() => clearFilterByKey(key)}
                        className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-blue-100 focus:outline-none"
                        aria-label={`Clear ${key}`}
                    >
                        <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                        >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    </div>
                )
                ))}
                <button
                onClick={clearFilters}
                className="ml-2 text-sm text-red-500 hover:underline"
                >
                Clear All
                </button>
            </div>
            )}

        </div>
          

      </div>

      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr className="text-sm sm:text-base">
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">User</th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Date</th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Request Type</th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Status</th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
          {loading ? (
              <tr className="border-b hover:bg-gray-100 dark:hover:bg-gray-800">
                <td colSpan={6} className="py-2 px-4 text-xs md:text-sm whitespace-nowrap">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2 mt-2"></div>
                    {/* <p className="text-sm text-center text-gray-500">Loading reports...</p> */}
                </td>
              </tr>
            ) : 
            filteredData.length === 0 ? (
            <tr>
                <td
                colSpan="6"
                className="text-center dark:text-white md:text-md text-sm text-gray-500 p-2">
                    No Data Found
                </td>
            </tr>
            ) : (
                filteredData.map((req) => (
                  <tr key={req.id} className="border-b dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white  hover:bg-gray-100">
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap">{req.user.name}</td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap">{req.request_date}</td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap">{req.requestable_type.split("\\").pop()}</td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap capitalize">{req.status}</td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center p-2">
                      <button onClick={() => { setSelectedRequest(req); setDrawerOpen(true); }} className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4">View</button>
                    </td>
                  </tr>
                ))
              )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isFiltering && filteredData?.length > 0 && (
        <ReactPaginate
          breakLabel="..."
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          pageCount={pagination.totalPages}
          renderOnZeroPageCount={null}
          nextLabel="Next >"
          previousLabel="< Prev"
          previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
          nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          containerClassName="flex items-center justify-center mt-4 font-poppins"
          pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
        />
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} ChilDiv="" ChildParent="" height_width="md:max-w-3xl md:h-max-h-3xl p-5" border_radius="md:rounded-3xl" showClose={false}>

        {/* Drawer panel */}
        <div className="bg-white dark:bg-gray-900 transform transition-all flex flex-col h-full">
          {/* Header */}
          <div className="px-6 py-5 border-b dark:border-gray-700 border-gray-100 flex justify-between items-center shrink-0">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Request Details</h2>
            <button 
              onClick={() => setDrawerOpen(false)} 
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <XCircle size={24} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {selectedRequest ? (
              <>
                {/* Request card */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 mb-6">
                  <div className="flex items-center mb-4">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <User size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-white">Requested by</p>
                      <p className="font-medium text-gray-900 dark:text-gray-500">{selectedRequest.user?.name || "Employee Name"}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start">
                      <Calendar size={18} className="text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-white">Request Date</p>
                        <p className="font-medium text-gray-900 dark:text-gray-500">{selectedRequest.request_date || "January 15, 2025"}</p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Briefcase size={18} className="text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-white">Leave Type</p>
                        <p className="font-medium text-gray-900 dark:text-gray-500">
                          {selectedRequest.requestable_type?.split("\\").pop() || "Annual Leave"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <Clock size={18} className="text-gray-400 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500 dark:text-white">Status</p>
                        <div className="flex items-center">
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                            selectedRequest.status === "approved" ? "bg-green-500" : 
                            selectedRequest.status === "rejected" ? "bg-red-500" : "bg-yellow-500"
                          }`}></span>
                          <p className="font-medium text-gray-90 dark:text-white capitalize">
                            {selectedRequest.status || "Pending"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes section */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-white mb-2">Reason</h3>
                  <p className="text-gray-600 dark:text-gray-500 text-sm">
                    {selectedRequest.notes || "No reason provided for this request."}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No request selected
              </div>
            )}
          </div>

          {/* Fixed Action Buttons */}
          {selectedRequest && (
            <div className="flex space-x-3">
              <button 
                  onClick={() => {
                  setIsModalOpen(true);
                  setActionType("approved");
                }}
                className="flex-1 dark:bg-black dark:border-green-500 dark:border bg-green-50 hover:bg-green-100 text-green-700 font-medium py-3 px-4 rounded-lg flex items-center justify-center transition-colors"
              >
                <CheckCircle size={18} className="mr-2" />
                Approve
              </button>
              <button 
                 onClick={() => {
                  setIsModalOpen(true);
                  setActionType("rejected");
                }}
                className="flex-1 dark:bg-black dark:border-red-500 dark:border bg-red-50 hover:bg-red-100 text-red-700 font-medium py-3 px-4 rounded-lg flex items-center justify-center transition-colors"
              >
                <XCircle size={18} className="mr-2" />
                Reject
              </button>
            </div>

          )}
        </div>

      </Drawer>
      
      {/* comment modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white font-poppins dark:bg-gray-900 p-6 rounded-lg shadow-xl w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4 dark:text-white">
              {actionType === "approved" ? "Approve Request" : "Reject Request"}
            </h2>

            <label htmlFor="approver-comments" className="block text-sm dark:text-gray-400 font-medium text-gray-700 mb-1">
              Add your comments
            </label>
            <textarea
              id="approver-comments"
              rows={3}
              className="w-full p-3 border dark:bg-black dark:text-white dark:border-gray-700 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your comments here..."
              value={approverComments}
              onChange={(e) => setApproverComments(e.target.value)}
            />

            <div className="flex justify-end mt-4 space-x-3">
              <button
                onClick={() => {
                  handleApproveReject(selectedRequest.id || 1, actionType);
                  setIsModalOpen(false);
                }}
                className={`px-4 py-2 rounded-lg text-white font-medium ${
                  actionType === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                }`}
              >
                Confirm
              </button>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setApproverComments("");
                  setActionType("");
                }}
                className="px-4 py-2 rounded-lg dark:bg-black dark:text-white bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


export default LeaveApproverPage;
